import React, { useState, useEffect } from 'react';
import { ArrowLeft, AlertTriangle, Calendar, FileText, Loader2 } from 'lucide-react';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { auth, db, functions } from '../firebase';
import ReactMarkdown from 'react-markdown';

const ReviewsAnalysis = ({ setCurrentPage }) => {
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [analyzing, setAnalyzing] = useState(false);
    const [error, setError] = useState(null);
    const [results, setResults] = useState(null);
    const [storedAnalyses, setStoredAnalyses] = useState([]);
    const [expandedAnalysis, setExpandedAnalysis] = useState(null);

    useEffect(() => {
        fetchStoredAnalyses();
    }, []);

    const fetchStoredAnalyses = async () => {
        try {
            const user = auth.currentUser;
            if (!user) return;

            const q = query(
                collection(db, "reviewsAnalysis"),
                where("userId", "==", user.uid),
                orderBy("timestamp", "desc")
            );

            const querySnapshot = await getDocs(q);
            const analyses = querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setStoredAnalyses(analyses);
        } catch (error) {
            console.error("Error fetching stored analyses:", error);
        }
    };

    const handleAnalyze = async () => {
        if (!startDate || !endDate) {
            setError("Please select both Start and End dates.");
            return;
        }

        setAnalyzing(true);
        setError(null);
        setResults(null);

        const analyzeReviewsFunc = httpsCallable(functions, 'analyzeReviews');

        try {
            const result = await analyzeReviewsFunc({ startDate, endDate });
            const data = result.data;

            if (data.success) {
                setResults(data.data);
                // Verify if backend saved it or we need to save. Backend saves it.
                // We just refresh the list.
                // Wait a moment for firestore to index/propagate
                setTimeout(fetchStoredAnalyses, 2000);
            } else {
                // Handle structured error in data if any
                if (data.message) setError(data.message);
            }

        } catch (err) {
            console.error(err);
            setError(`Analysis failed: ${err.message}`);
        } finally {
            setAnalyzing(false);
        }
    };

    const toggleAnalysis = (id) => {
        setExpandedAnalysis(expandedAnalysis === id ? null : id);
    };

    return (
        <div className="max-w-4xl mx-auto p-4">
            <button onClick={() => setCurrentPage('home')} className="mb-4 flex items-center text-[rgb(100,204,201)] hover:underline">
                <ArrowLeft className="mr-2" /> Back to Dashboard
            </button>

            <h2 className="text-2xl font-bold mb-6">Review Analysis</h2>

            <div className="bg-white p-6 rounded-lg shadow-md mb-8">
                <div className="mb-4 p-4 bg-blue-50 border-l-4 border-blue-400 text-blue-700">
                    <div className="flex">
                        <AlertTriangle className="mr-2" size={20} />
                        <p><strong>Note:</strong> Data is automatically retrieved from the shared Google Sheet. You only need to select the date range.</p>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                        <div className="relative">
                            <Calendar className="absolute left-3 top-2.5 text-gray-400" size={16} />
                            <input
                                type="date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-[rgb(100,204,201)] focus:ring focus:ring-[rgb(100,204,201)] focus:ring-opacity-50 border p-2"
                            />
                        </div>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                        <div className="relative">
                            <Calendar className="absolute left-3 top-2.5 text-gray-400" size={16} />
                            <input
                                type="date"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-[rgb(100,204,201)] focus:ring focus:ring-[rgb(100,204,201)] focus:ring-opacity-50 border p-2"
                            />
                        </div>
                    </div>
                </div>

                <button
                    onClick={handleAnalyze}
                    disabled={analyzing}
                    className={`w-full flex justify-center items-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${analyzing ? 'bg-gray-400' : 'bg-[rgb(100,204,201)] hover:bg-[rgb(90,184,181)]'} focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[rgb(100,204,201)]`}
                >
                    {analyzing ? (
                        <>
                            <Loader2 className="animate-spin mr-2" size={18} />
                            Analyzing...
                        </>
                    ) : 'Start Analysis'}
                </button>

                {error && (
                    <div className="mt-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
                        <span className="block sm:inline">{error}</span>
                    </div>
                )}
            </div>

            {(results || storedAnalyses.length > 0) && (
                <div className="space-y-6">
                    {results && (
                        <div className="bg-white p-6 rounded-lg shadow-md border-2 border-[rgb(100,204,201)]">
                            <h3 className="text-xl font-bold mb-4">Current Analysis Result</h3>
                            <div className="text-sm text-gray-500 mb-4">
                                Range: {results.dateRange.start} to {results.dateRange.end}
                            </div>
                            {results.analyses.length > 0 ? results.analyses.map((analysis, idx) => (
                                <div key={idx} className="mb-8 p-4 bg-gray-50 rounded">
                                    <h4 className="text-lg font-bold mb-2 text-gray-800">{analysis.companyName} ({analysis.reviewCount} reviews)</h4>
                                    {analysis.error ? (
                                        <p className="text-red-600">{analysis.error}</p>
                                    ) : (
                                        <div className="prose max-w-none text-gray-700">
                                            <ReactMarkdown>{analysis.analysis}</ReactMarkdown>
                                        </div>
                                    )}
                                </div>
                            )) : (
                                <p>No analyses available for this range.</p>
                            )}
                        </div>
                    )}

                    <h3 className="text-xl font-semibold mt-8 mb-4">Past Analyses</h3>
                    {storedAnalyses.map((analysis) => (
                        <div key={analysis.id} className="bg-white border rounded-lg overflow-hidden shadow-sm">
                            <div
                                className="flex justify-between items-center p-4 bg-gray-50 cursor-pointer hover:bg-gray-100"
                                onClick={() => toggleAnalysis(analysis.id)}
                            >
                                <div className="flex items-center space-x-3">
                                    <FileText size={20} className="text-gray-500" />
                                    <div>
                                        <h4 className="font-medium text-gray-900">Analysis run on {new Date(analysis.timestamp?.seconds * 1000).toLocaleDateString()}</h4>
                                        <p className="text-sm text-gray-500">Range: {analysis.dateRange?.start} - {analysis.dateRange?.end}</p>
                                    </div>
                                </div>
                            </div>
                            {expandedAnalysis === analysis.id && (
                                <div className="p-6 border-t">
                                    {analysis.analyses?.map((a, idx) => (
                                        <div key={idx} className="mb-6 last:mb-0">
                                            <h5 className="font-bold text-lg mb-2">{a.companyName}</h5>
                                            {a.error ? <p className="text-red-500">{a.error}</p> : (
                                                <div className="prose max-w-none text-gray-700 text-sm">
                                                    <ReactMarkdown>{a.analysis}</ReactMarkdown>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default ReviewsAnalysis;
